// Tasman Holiday Parks booking strategy (shared by both AU and NZ splits).
//
// Tasman's Newbook white-label page accepts the child category via
// `force_category_id` (already in `site.reserveUrl`) plus a date window:
//
//   available_from=<dd MMM yyyy>
//   available_to=<dd MMM yyyy>
//   nights=<N>
//   adults=<N>
//
// Newbook serializes spaces as `+` in the browser URL, e.g. `13+Aug+2026`.
// ISO format also works but the long format matches the booking page's
// `value` attributes for fingerprint stability.

import { format } from "date-fns";
import {
  resolveCheckout,
  resolveMinCapacity,
  resolveMinStayLength,
} from "./_context.js";

function formatNewbookDate(date) {
  return format(date, "dd MMM yyyy");
}

export default {
  buildBookingUrl(ctx) {
    const { site, arrivalDate } = ctx;
    const checkout = resolveCheckout(ctx);
    const nights = resolveMinStayLength(ctx);
    const adults = resolveMinCapacity(ctx);

    const url = new URL(site.reserveUrl);
    url.searchParams.set("available_from", formatNewbookDate(arrivalDate));
    url.searchParams.set("available_to", formatNewbookDate(checkout));
    url.searchParams.set("nights", String(nights));
    url.searchParams.set("adults", String(adults));
    return url.toString();
  },

  buildPreviewUrl(site) {
    // Strip `force_category_id` (and any other params) so the preview
    // opens the park summary page where the user sees ALL categories,
    // not the booking flow pre-filtered to one cat. The bare park URL
    // (`/<slug>/`) renders the chart with every available cat box —
    // closer to a "view map" overview than a single-cat reserve flow.
    const url = new URL(site.reserveUrl);
    url.search = "";
    return url.toString();
  },

  describeBookingInputs() {
    return {
      inputs: {
        arrivalDate: {
          status: "required",
          notes: "Encoded as &available_from=<dd MMM yyyy>.",
        },
        departureDate: {
          status: "required",
          notes:
            "Encoded as &available_to=<dd MMM yyyy>; resolved from arrival + minStayLength when not supplied.",
        },
        ref: {
          status: "ignored",
          notes:
            "site.reserveUrl already encodes Newbook force_category_id for the child category.",
        },
        partySize: {
          status: "defaulted",
          defaultValue: 1,
          notes:
            "Encoded as &adults=<N>; defaults to AvailabilityRequest.minCapacity, then 1.",
        },
      },
    };
  },
};
